import React, { useState, useEffect } from "react";
import * as XLSX from "xlsx";
import {
  FunnelChart,
  Funnel,
  LabelList,
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
} from "recharts";
import axios from "axios";

// Custom Label component for the funnel chart - showing name and value
const CustomLabel = (props) => {
  const { x, y, width, height, value, name } = props;
  return (
    <g>
      <text
        x={x + width / 2}
        y={y + height / 2 - 12}
        textAnchor="middle"
        dominantBaseline="middle"
        fill="black"
        className="text-base font-medium"
      >
        {name}
      </text>
      <text
        x={x + width / 2}
        y={y + height / 2 + 12}
        textAnchor="middle"
        dominantBaseline="middle"
        fill="black"
        className="text-xl font-bold"
      >
        {value}
      </text>
    </g>
  );
};

//--- This component is not being used but kept for reference

// Flow Chart component (unchanged)
const FlowChart = ({
  totalLeads,
  enquiryLeads,
  customerLeads,
  lostLeads,
  fresh_leads,
  onStatusClick,
}) => {
  const [hoveredElement, setHoveredElement] = useState(null);

  // Calculate percentages based on the API data structure
  const freshPercentage = totalLeads
    ? ((fresh_leads / totalLeads) * 100).toFixed(0)
    : 0;
  const enquiryPercentage = totalLeads
    ? ((enquiryLeads / totalLeads) * 100).toFixed(0)
    : 0;
  const customerPercentage = enquiryLeads
    ? ((customerLeads / enquiryLeads) * 100).toFixed(0)
    : 0;
  const lostPercentage = enquiryLeads
    ? ((lostLeads / enquiryLeads) * 100).toFixed(0)
    : 0;

  const Box = ({ title, value, color, x, y, id, status }) => (
    <g
      onMouseEnter={() => setHoveredElement(id)}
      onMouseLeave={() => setHoveredElement(null)}
      onClick={() => onStatusClick(status)}
      className="transition-transform duration-300 cursor-pointer"
    >
      <g transform={`translate(${x}, ${y})`}>
        <rect
          width={120}
          height={80}
          rx={12}
          className={`${color} shadow-lg transition-all duration-300`}
          style={{
            transform: hoveredElement === id ? "scale(1.05)" : "scale(1)",
            transformOrigin: "center",
            filter:
              hoveredElement === id
                ? "brightness(1.1) drop-shadow(0 4px 6px rgba(0,0,0,0.2))"
                : "",
          }}
        />
        <text
          x={60}
          y={35}
          textAnchor="middle"
          className="font-semibold text-lg fill-white"
        >
          {title}
        </text>
        <text
          x={60}
          y={60}
          textAnchor="middle"
          className="font-bold text-2xl fill-white"
        >
          {value}
        </text>
      </g>
    </g>
  );

  return (
    <div className="rounded-lg shadow-md p-6">
      <h3 className="text-lg font-semibold mb-6">Lead Flow Distribution</h3>
      <div className="flex justify-center">
        <svg width="600" height="600" className="overflow-visible">
          {/* Total Leads */}
          <Box
            id="total"
            title="Total Leads"
            value={totalLeads}
            color="fill-blue-500"
            x={240}
            y={20}
            status="all"
          />

          {/* Connection to Fresh Leads */}
          <line
            x1="300"
            y1="100"
            x2="300"
            y2="140"
            className="stroke-gray-300 stroke-2"
          />
          <text x="320" y="130" className="text-gray-600 font-medium">
            {freshPercentage}%
          </text>

          {/* Fresh Leads */}
          <Box
            id="fresh"
            title="Fresh Leads"
            value={fresh_leads}
            color="fill-yellow-500"
            x={240}
            y={140}
            status="fresh"
          />

          {/* Connection to Enquiry */}
          <line
            x1="300"
            y1="220"
            x2="300"
            y2="260"
            className="stroke-gray-300 stroke-2"
          />
          <text x="320" y="250" className="text-gray-600 font-medium">
            {enquiryPercentage}%
          </text>

          {/* Enquiry */}
          <Box
            id="enquiry"
            title="Enquiry"
            value={enquiryLeads}
            color="fill-green-500"
            x={240}
            y={260}
            status="enquiry"
          />

          {/* Connections to Customer and Lost */}
          <line
            x1="300"
            y1="340"
            x2="300"
            y2="380"
            className="stroke-gray-300 stroke-2"
          />
          <path
            d="M 300 380 Q 300 420, 180 420"
            className="stroke-gray-300 stroke-2 fill-none"
          />
          <path
            d="M 300 380 Q 300 420, 420 420"
            className="stroke-gray-300 stroke-2 fill-none"
          />

          {/* Customer */}
          <Box
            id="customer"
            title="Customer"
            value={customerLeads}
            color="fill-purple-500"
            x={120}
            y={420}
            status="customer"
          />

          {/* Lost */}
          <Box
            id="lost"
            title="Lost"
            value={lostLeads}
            color="fill-red-500"
            x={360}
            y={420}
            status="lost"
          />

          {/* Percentages for Customer and Lost */}
          <text x="200" y="400" className="text-gray-600 font-medium">
            {customerPercentage}%
          </text>
          <text x="400" y="400" className="text-gray-600 font-medium">
            {lostPercentage}%
          </text>
        </svg>
      </div>
    </div>
  );
};

// LeadTable Component (unchanged)
const LeadTable = ({ selectedStatus = "all" }) => {
  const [showTable, setShowTable] = useState(false);
  const [tableData, setTableData] = useState([]);
  const [tableLoading, setTableLoading] = useState(false);
  const [tableError, setTableError] = useState(null);
  const [isExporting, setIsExporting] = useState(false);

  const fetchTableData = async () => {
    try {
      setTableLoading(true);

      const response = await axios.get(
        `${process.env.REACT_APP_BASE_URL}/lead/api/`,
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        }
      );
      let data = response.data.results.Leads || [];

      // Filter data based on selected status
      if (selectedStatus !== "all") {
        data = data.filter((lead) => lead.status === selectedStatus);
      }

      setTableData(data);
      setTableError(null);
    } catch (err) {
      setTableError(err.message || "Failed to fetch lead data");
      setTableData([]);
    } finally {
      setTableLoading(false);
    }
  };

  // Fetch data when status changes or table is shown
  useEffect(() => {
    if (showTable) {
      fetchTableData();
    }
  }, [selectedStatus, showTable]);

  const toggleTable = () => {
    setShowTable(!showTable);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString();
  };

  // Function to export table data to Excel
  const exportToExcel = async () => {
    try {
      setIsExporting(true);

      // If table is not shown, fetch data first
      if (!showTable) {
        await fetchTableData();
      }

      // Create worksheet from table data
      const worksheet = XLSX.utils.json_to_sheet(
        tableData.map((lead) => ({
          ID: lead.id,
          Name: lead.parent_info?.name || "N/A",
          Status: lead.status || "N/A",
          "Lead Type": lead.lead_type || "N/A",
          "Created Date": formatDate(lead.created_at),
          "Created By": lead.created_by || "N/A",
          Branch: lead.branch || "N/A",
        }))
      );

      // Create workbook and add the worksheet
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, "Leads");

      // Create filename based on status and date
      const statusText =
        selectedStatus === "all"
          ? "All_Leads"
          : `${
              selectedStatus.charAt(0).toUpperCase() + selectedStatus.slice(1)
            }_Leads`;
      const dateStr = new Date().toISOString().split("T")[0];
      const fileName = `${statusText}_${dateStr}.xlsx`;

      // Generate and download Excel file
      XLSX.writeFile(workbook, fileName);
    } catch (error) {
      console.error("Failed to export data to Excel:", error);
      // Optionally show error message to user
    } finally {
      setIsExporting(false);
    }
  };

  return (
    <div className="rounded-lg shadow-md p-6 mt-6">
      <div className="flex justify-between items-center mb-4">
        <h3 className="text-lg font-semibold">
          {selectedStatus === "all"
            ? "All Leads"
            : `${
                selectedStatus.charAt(0).toUpperCase() + selectedStatus.slice(1)
              } Leads`}
        </h3>
        <div className="flex space-x-3">
          <button
            onClick={exportToExcel}
            disabled={isExporting || (tableData.length === 0 && !showTable)}
            className={`px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 transition-colors flex items-center ${
              isExporting || (tableData.length === 0 && !showTable)
                ? "opacity-50 cursor-not-allowed"
                : ""
            }`}
          >
            {isExporting ? (
              <>
                <svg
                  className="animate-spin -ml-1 mr-2 h-4 w-4 text-white"
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                >
                  <circle
                    className="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeWidth="4"
                  ></circle>
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                  ></path>
                </svg>
                Exporting...
              </>
            ) : (
              <>
                <svg
                  className="w-4 h-4 mr-2"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4"
                  ></path>
                </svg>
                Export to Excel
              </>
            )}
          </button>

          <button
            onClick={toggleTable}
            className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
          >
            {showTable ? "Hide Details" : "Show Details"}
          </button>
        </div>
      </div>

      {tableLoading && (
        <div className="text-center py-4">Loading lead details...</div>
      )}

      {tableError && (
        <div className="text-red-600 bg-red-50 p-4 rounded-md mb-4">
          Error: {tableError}
        </div>
      )}

      {showTable && !tableLoading && (
        <div className="overflow-x-auto">
          <table className="min-w-full bg-white border border-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 border-b border-gray-200 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  ID
                </th>
                <th className="px-6 py-3 border-b border-gray-200 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Name
                </th>
                <th className="px-6 py-3 border-b border-gray-200 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 border-b border-gray-200 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Lead Type
                </th>
                <th className="px-6 py-3 border-b border-gray-200 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Created Date
                </th>
                <th className="px-6 py-3 border-b border-gray-200 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Created By
                </th>
                <th className="px-6 py-3 border-b border-gray-200 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Branch
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {tableData.map((lead) => (
                <tr key={lead.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                    {lead.id}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-blue-600 hover:text-blue-800">
                    {lead.parent_info?.name || "N/A"}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span
                      className={`px-2 py-1 text-xs font-semibold rounded-full ${
                        lead.status === "customer"
                          ? "bg-green-100 text-green-800"
                          : lead.status === "enquiry"
                          ? "bg-blue-100 text-blue-800"
                          : lead.status === "fresh"
                          ? "bg-yellow-100 text-yellow-800"
                          : "bg-gray-100 text-gray-800"
                      }`}
                    >
                      {lead.status || "N/A"}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {lead.lead_type || "N/A"}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {formatDate(lead.created_at)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {lead.created_by || "N/A"}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {lead.branch || "N/A"}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

// Main component with updated funnel implementation
const NewLeadWithFlow = ({ selectedYear, selectedMonth, selectedAdmin }) => {
  const [leadData, setLeadData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedStatus, setSelectedStatus] = useState("all");

  useEffect(() => {
    const fetchLeadData = async () => {
      try {
        setLoading(true);
        const params = new URLSearchParams();
        if (selectedYear) params.append("year", selectedYear);
        if (selectedMonth && selectedMonth !== "")
          params.append("month", selectedMonth);
        if (selectedAdmin && selectedAdmin !== "all")
          params.append("created_by", selectedAdmin);

        const response = await axios.get(
          `${
            process.env.REACT_APP_BASE_URL
          }/dashboard/api/lead_summary/?${params.toString()}`
        );

        setLeadData(response.data || null);
        setError(null);
      } catch (err) {
        setError(err.message || "Failed to fetch lead data");
        setLeadData(null);
      } finally {
        setLoading(false);
      }
    };

    fetchLeadData();
  }, [selectedYear, selectedMonth, selectedAdmin]);

  if (loading) return <div className="p-6 text-center">Loading...</div>;
  if (error)
    return <div className="p-6 text-center text-red-600">Error: {error}</div>;
  if (!leadData) return null;

  const cardData = [
    {
      title: "Fresh to Enquiry",
      value: leadData.fresh_to_enquiry_conversion || 0,
      color: "text-blue-600",
    },
    {
      title: "Enquiry to Customer",
      value: leadData.enquiry_to_customer_conversion || 0,
      color: "text-green-600",
    },
    {
      title: "Lost Lead",
      value: leadData.lost_percentage || 0,
      color: "text-red-600",
    },
  ];

  // Define funnel colors to match the purple color scheme in the image
  const funnelColors = ["#5E4AE3", "#9D84FF", "#A78BFA"];

  // Create funnel data with exactly 3 levels as shown in the image
  const funnelData = [
    {
      name: "Fresh Leads",
      value: Number(leadData.fresh_leads) || 0,
      fill: funnelColors[0],
    },
    {
      name: "Enquiry",
      value: Number(leadData.enquiry_leads) || 0,
      fill: funnelColors[1],
    },
    {
      name: "Customer",
      value: Number(leadData.customer_leads) || 0,
      fill: funnelColors[2],
    },
  ];

  const monthlyData = [
    {
      name: "Total",
      customer: leadData.customer_percentage || 0,
      lost: leadData.lost_percentage || 0,
    },
  ];

  const handleStatusClick = (status) => {
    setSelectedStatus(status);
  };

  return (
    <div className="p-6 bg-gray-100 min-h-screen space-y-6">
      {/* Conversion Cards */}
      <div className="grid grid-cols-3 gap-4">
        {cardData.map((card, index) => (
          <div
            key={index}
            className="p-4 border rounded-lg shadow-md bg-gradient-to-br from-gray-100 to-gray-200 hover:shadow-lg"
          >
            <div className="text-lg font-medium">{card.title}</div>
            <div className={`text-2xl font-bold ${card.color}`}>
              {card.value.toFixed(2)}%
            </div>
          </div>
        ))}
      </div>

      {/* Funnel and Bar Charts */}
      <div className="grid bg-white grid-cols-2 gap-6">
        {/* Custom Diamond-shaped Funnel Chart - All labels inside */}
        <div className="bg-white rounded-lg shadow-md p-4 h-[500px] relative">
          <h2 className="text-lg font-semibold mb-4">Sales Funnel</h2>

          {/* Custom SVG implementation with all labels inside */}
          <div className="w-full h-[400px] flex items-center justify-center">
            <svg
              width="100%"
              height="100%"
              viewBox="0 0 600 400"
              preserveAspectRatio="xMidYMid meet"
              className="overflow-visible"
            >
              {/* Fresh Leads - Top funnel */}
              <polygon
                points="225,20 375,20 350,120 250,120"
                fill="#5E4AE3"
                className="transition-all duration-300"
              />
              <text
                x="300"
                y="65"
                textAnchor="middle"
                className="text-base font-medium"
              >
                Fresh Leads
              </text>
              <text
                x="300"
                y="95"
                textAnchor="middle"
                className="text-2xl font-bold"
              >
                {leadData.fresh_leads || 0}
              </text>

              {/* Enquiry - Middle funnel (wide diamond) */}
              <polygon
                points="250,120 350,120 500,200 100,200"
                fill="#9D84FF"
                className="transition-all duration-300"
              />

              {/* Enquiry label - now inside the middle section */}
              <text
                x="300"
                y="165"
                textAnchor="middle"
                className="text-base font-medium"
              >
                Enquiry
              </text>
              <text
                x="300"
                y="195"
                textAnchor="middle"
                className="text-2xl font-bold"
              >
                {leadData.enquiry_leads || 0}
              </text>

              {/* Customer - Bottom funnel */}
              <polygon
                points="100,200 500,200 300,360"
                fill="#A78BFA"
                className="transition-all duration-300"
              />
              <text
                x="300"
                y="260"
                textAnchor="middle"
                className="text-base font-medium"
              >
                Customer
              </text>
              <text
                x="300"
                y="290"
                textAnchor="middle"
                className="text-2xl font-bold"
              >
                {leadData.customer_leads || 0}
              </text>
            </svg>
          </div>
        </div>

        {/* Lead Distribution Chart */}
        <div className="bg-white rounded-lg shadow-md p-4">
          <h3 className="text-lg font-semibold mb-4">Lead Distribution</h3>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={monthlyData}>
              <defs>
                <linearGradient
                  id="customerGradient"
                  x1="0"
                  y1="0"
                  x2="0"
                  y2="1"
                >
                  <stop offset="0%" stopColor="#34D399" />
                  <stop offset="100%" stopColor="#059669" />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="name" />
              <YAxis domain={[0, 100]} />
              <Tooltip formatter={(value) => [`${value}%`]} />
              <Legend />
              <Bar
                dataKey="customer"
                name="Customer"
                stackId="a"
                fill="url(#customerGradient)"
                radius={[4, 4, 0, 0]}
              />
              <Bar
                dataKey="lost"
                name="Lost"
                stackId="a"
                fill="#ef4444"
                radius={[4, 4, 0, 0]}
              />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Flow Chart */}
      <FlowChart
        totalLeads={leadData.total_leads}
        fresh_leads={leadData.fresh_leads}
        enquiryLeads={leadData.enquiry_leads}
        customerLeads={leadData.customer_leads}
        lostLeads={leadData.lost_leads}
        onStatusClick={handleStatusClick}
      />

      {/* Lead Table */}
      <LeadTable selectedStatus={selectedStatus} />
    </div>
  );
};

export default NewLeadWithFlow;
